const time = value => value ? Date.parse(value) : NaN;
const normalized = value => String(value || '').trim().toUpperCase();

// Older snapshots stored the individual task estimates and shop totals but not
// explicit sourceTasks. Only recover attribution when the hours reconcile.
function contributingTasks(forecast, labor) {
  if (Array.isArray(labor.sourceTasks)) return labor.sourceTasks;
  const tasks = (forecast.reasons || []).filter(reason =>
    ['inspection', '8035', 'customTask'].includes(reason.type)
    && reason.rtgImpact === 'current_blocker' && !reason.laborDemandCountedOn)
    .map(reason => {
      const rows = Array.isArray(reason.laborRequirements) ? reason.laborRequirements : [
        { shopName: reason.shop, laborHours: reason.durationHours },
      ];
      const workHours = rows.reduce((sum, row) => {
        const pool = row.laborSourceType === 'pool';
        const name = pool ? row.laborPoolName || row.shopName : row.shopName;
        return sum + (pool === (labor.sourceType === 'pool') && normalized(name) === normalized(labor.shop)
          ? Number(row.laborHours || 0) : 0);
      }, 0);
      return { type: reason.type, id: reason.id, label: reason.label || reason.noun || reason.discrepancy, jcn: reason.jcn, workHours };
    }).filter(task => task.id && task.label && task.workHours > 0);
  const total = tasks.reduce((sum, task) => sum + task.workHours, 0);
  return Math.abs(total - Number(labor.workHours)) <= 0.02 ? tasks : [];
}

export function estimatedRtgDriverItems(row, plannedItems, classifyTask = () => null) {
  const forecast = row.forecast;
  if (!forecast || forecast.authorizedReadyNow) return [];
  const end = time(forecast.projectedReadyAt);
  const labor = (forecast.reasons || []).find(reason => reason.type === 'labor'
    && normalized(forecast.primaryDriver) === normalized(`${reason.shop} workload`));
  if (!labor || !Number.isFinite(end)) return [];
  const tasks = contributingTasks(forecast, labor).filter(task => task.id && task.label && task.workHours > 0);
  if (!tasks.length) return [];
  const returnGate = (forecast.availabilityWindows || []).filter(window => window.kind === 'offstation_return')
    .map(window => time(window.endAt)).filter(Number.isFinite);
  const start = time(labor.forecastStartAt) || Math.max(time(forecast.calculatedAt), ...returnGate);
  if (!Number.isFinite(start) || start >= end) return [];

  // These are uncovered portions of the existing forecast, not invented task
  // start/finish dates or a proposed execution order. Alert does not hide Mx.
  let gaps = [[start, end]];
  for (const item of plannedItems.filter(item => item.reason.type !== 'acftPlan')) {
    const from = item.plannedStartMs;
    const to = item.plannedFinishMs;
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) continue;
    gaps = gaps.flatMap(([a, b]) => to <= a || from >= b ? [[a, b]] : [
      ...(from > a ? [[a, from]] : []), ...(to < b ? [[to, b]] : []),
    ]);
  }
  const taskGroups = new Map();
  tasks.forEach(task => {
    const classified = classifyTask(task) || {};
    const category = classified.key && classified.className
      ? classified
      : { key: 'other', label: 'Other Drivers', className: 'is-driver-other' };
    if (!taskGroups.has(category.key)) taskGroups.set(category.key, { category, tasks: [] });
    taskGroups.get(category.key).tasks.push(task);
  });
  return gaps.flatMap(([from, to], gapIndex) => [...taskGroups.values()].map(({ category, tasks: categoryTasks }) => {
    const driverName = category.label || 'Other Drivers';
    const names = [...new Set(categoryTasks.map(task => `${task.label}${task.jcn ? ` | JCN ${task.jcn}` : ''}`))];
    const label = [driverName, 'Estimated timing within the current readiness forecast; individual task dates/order are not assigned.',
      'Contributing tasks:', ...names].join('\n');
    return {
      reason: { type: 'forecastEstimate', id: `${category.key || 'other'}-${gapIndex}` },
      plannedStartMs: from, plannedFinishMs: to, label, driverName,
      driverCategory: category,
      shortLabel: driverName, estimated: true,
    };
  }));
}
